import { useMutation } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type { AuditQuery } from "../../shared/audit-schemas";
import { listOperationsAudit, listWorkspaceAudit } from "../api/audit-client";
import { Button } from "../components/ui/button";
import { collectAuditExport } from "./export-csv";

export function AuditExport({
  query,
  audience,
  organizationId,
}: {
  query: AuditQuery;
  audience: "customer" | "operations";
  organizationId?: string | undefined;
}) {
  const activeRef = useRef<AbortController | null>(null);
  const mounted = useCallback(() => () => activeRef.current?.abort(), []);
  const [count, setCount] = useState(0);
  const [controller, setController] = useState<AbortController | null>(null);
  const download = useMutation({
    mutationFn: async () => {
      const active = new AbortController();
      activeRef.current = active;
      setController(active);
      setCount(0);
      try {
        const csv = await collectAuditExport({
          query,
          now: Math.floor(Date.now() / 1000),
          signal: active.signal,
          progress: setCount,
          read: (input, signal) =>
            audience === "customer" && organizationId
              ? listWorkspaceAudit(organizationId, input, signal)
              : listOperationsAudit({ ...input, organizationId }, signal),
        });
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } finally {
        setController(null);
      }
    },
  });
  return (
    <div ref={mounted} className="flex max-w-sm flex-wrap items-center gap-2">
      <Button
        variant="outline"
        disabled={download.isPending}
        onClick={() => {
          download.mutate();
        }}
      >
        <Download />
        {download.isPending ? `Exporting ${count}…` : "Export CSV"}
      </Button>
      {download.isPending ? (
        <Button variant="ghost" onClick={() => controller?.abort()}>
          Cancel export
        </Button>
      ) : null}
      {download.error ? (
        <p role="alert" className="m-0 basis-full text-sm text-destructive">
          {download.error.name === "AbortError" ? "Export cancelled." : download.error.message}
        </p>
      ) : null}
      {download.isSuccess ? (
        <p role="status" className="m-0 basis-full text-xs text-muted-foreground">
          Exported {count} events.
        </p>
      ) : null}
    </div>
  );
}
